import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Scale, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useSessionEscalations, ESCALATION_DEPARTMENT_OPTIONS } from '@/hooks/useSessionEscalations';
import { useAppUsers } from '@/hooks/useAppUsers';
import { useCurrentAppUser } from '@/hooks/useCurrentAppUser';

interface EscalateModalProps {
  sessionId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  defaultPriority?: string;
}

export function EscalateModal({ 
  sessionId, 
  open, 
  onOpenChange, 
  onSuccess,
  defaultPriority = 'normal',
}: EscalateModalProps) {
  const { data: currentAppUser } = useCurrentAppUser();
  const { users: appUsers } = useAppUsers();
  const { createEscalation, isCreating } = useSessionEscalations(sessionId);

  // Form states
  const [departmentId, setDepartmentId] = useState('');
  const [userId, setUserId] = useState('');
  const [question, setQuestion] = useState('');
  const [priority, setPriority] = useState(defaultPriority);

  // Filter users by selected department
  const departmentUsers = appUsers.filter(u => 
    u.active && 
    u.department_id === departmentId &&
    u.id !== currentAppUser?.id
  );

  const getUserName = (user: typeof appUsers[0]) => {
    return user.display_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || 'Sem nome';
  };

  const resetForm = () => {
    setDepartmentId('');
    setUserId('');
    setQuestion('');
    setPriority(defaultPriority);
  };

  const handleSubmit = async () => {
    if (!departmentId) {
      toast.error('Selecione um departamento');
      return;
    }
    if (!userId) {
      toast.error('Selecione um usuário');
      return;
    }
    if (question.trim().length < 10) {
      toast.error('A pergunta deve ter pelo menos 20 caracteres');
      return;
    }

    try {
      await createEscalation({
        session_id: sessionId,
        escalated_to: userId,
        question: question,
        priority: priority,
        department_id: departmentId,
      });
      toast.success('Escalação criada - Task atribuída automaticamente');
      onOpenChange(false);
      resetForm();
      onSuccess?.();
    } catch (err) {
      toast.error('Erro ao criar escalação');
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      resetForm();
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Scale className="h-5 w-5" />
            Escalar para Outro Departamento
          </DialogTitle>
          <DialogDescription>
            Uma task será criada automaticamente para o responsável.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {/* Departamento */}
          <div className="space-y-2">
            <Label>Departamento *</Label>
            <Select value={departmentId} onValueChange={(v) => {
              setDepartmentId(v);
              setUserId('');
            }}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione o departamento" />
              </SelectTrigger>
              <SelectContent>
                {ESCALATION_DEPARTMENT_OPTIONS.map(dept => (
                  <SelectItem key={dept.id} value={dept.id}>
                    {dept.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Usuário (filtrado por departamento) */}
          <div className="space-y-2">
            <Label>Atribuir para *</Label>
            <Select 
              value={userId} 
              onValueChange={setUserId}
              disabled={!departmentId}
            >
              <SelectTrigger>
                <SelectValue placeholder={
                  !departmentId 
                    ? "Selecione um departamento primeiro" 
                    : "Selecione um usuário"
                } />
              </SelectTrigger>
              <SelectContent>
                {departmentUsers.length === 0 ? (
                  <SelectItem value="none" disabled>
                    Nenhum usuário neste departamento
                  </SelectItem>
                ) : (
                  departmentUsers.map(user => (
                    <SelectItem key={user.id} value={user.id}>
                      {getUserName(user)}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Prioridade */}
          <div className="space-y-2">
            <Label>Prioridade</Label>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="normal">Normal (SLA 8h)</SelectItem>
                <SelectItem value="high">Alta (SLA 4h)</SelectItem>
                <SelectItem value="urgent">Urgente (SLA 2h)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Pergunta */}
          <div className="space-y-2">
            <Label>Pergunta * (mínimo 10 caracteres)</Label>
            <Textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="Descreva detalhadamente sua dúvida ou solicitação..."
              rows={4}
              className={question.length > 0 && question.length < 10 
                ? 'border-destructive' 
                : ''
              }
            />
            <p className="text-xs text-muted-foreground">
              {question.length}/10 caracteres mínimos
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancelar
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={
              isCreating || 
              !departmentId || 
              !userId || 
              question.trim().length < 10
            }
          >
            {isCreating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Escalar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
